import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { DisplayProps } from 'styled-system';
import { display, position, space } from 'styled-system';

import { Link } from 'common/components/Link';
import { CaptionRegularCR1 } from 'common/components/Typography';
import type { Language } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import { cursor, pointerEvents, textTransform } from 'common/theme/system-utilities';
import { getUrlPrefix } from 'common/utils/url';
import { trackLanguageSelectorClick } from './tracking';

interface LanguageSwitchProps {
  languages: Language[];
}

const LanguageSwitchWrapper = styled.div<DisplayProps & SpaceProps>`
  ${display};
  ${space};
  align-self: flex-end;
  flex: none;
`;

const languageSwitchWrapperStyles: ComponentProps<typeof LanguageSwitchWrapper> = {
  display: ['none', null, 'inline-flex'],
  ml: [null, 'sp_2', 'sp_12'],
};

const LanguageLabel = styled(CaptionRegularCR1)<any>`
  ${pointerEvents};
  ${position};
  ${cursor};
  ${textTransform};

  ${({ index, theme }) =>
    index !== 0 &&
    `
    padding-left: ${theme.space.sp_4}px;

    &::before {
      content: '';
      display: inline-block;
      width: 1px;
      height: 8px;
      margin-right: ${theme.space.sp_4}px;
      background-color: currentColor;
    }
    `}
`;

const currentLanguageStyles = {
  fontWeight: 'bold',
  position: 'relative',
  textTransform: 'capitalize',
};

const languageSelectorStyles = {
  ...currentLanguageStyles,
  as: Link,
  cursor: 'pointer',
  fontWeight: 'normal',
};

const LanguageSwitch = ({ languages }: LanguageSwitchProps) => {
  if (!languages || languages?.length < 2) {
    return null;
  }

  return (
    <LanguageSwitchWrapper {...languageSwitchWrapperStyles}>
      {languages.map(({ language, pathPrefix, isCurrent }, index) =>
        isCurrent ? (
          <LanguageLabel
            data-testid="current-language"
            key={language}
            index={index}
            {...currentLanguageStyles}
          >
            {language}
          </LanguageLabel>
        ) : (
          <LanguageLabel
            key={language}
            index={index}
            data-testid="language-selector-link"
            href={getUrlPrefix(pathPrefix, isCurrent)}
            current={isCurrent}
            onClick={() => trackLanguageSelectorClick(language)}
            {...languageSelectorStyles}
          >
            {language}
          </LanguageLabel>
        )
      )}
    </LanguageSwitchWrapper>
  );
};

export default LanguageSwitch;
